import type { GroupMember, GroupMemberOffer } from '@stalvi/contracts';
import { ExternalLink } from 'lucide-react';
import { formatCLP } from '../lib/format';
import { ProductThumb } from './ProductThumb';

function sizeLabel(netContent: string | null, unit: string | null): string | null {
  const parts = [netContent, unit].filter(Boolean);
  return parts.length ? parts.join(' ') : null;
}

function OfferLine({ offer }: { offer: GroupMemberOffer }) {
  const price = offer.bestPrice != null ? formatCLP(offer.bestPrice) : '—';
  const name = offer.retailerName ?? offer.retailerId ?? '—';
  return (
    <li className="flex items-center justify-between gap-2 text-sm" data-testid="member-offer">
      <span className="flex items-center gap-1.5 min-w-0">
        {offer.url ? (
          <a
            href={offer.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-orange-600 hover:underline truncate"
          >
            {name}
            <ExternalLink size={12} className="shrink-0" />
          </a>
        ) : (
          <span className="truncate">{name}</span>
        )}
        {offer.available === false && <span className="pill pill-neutral text-[11px]">Sin stock</span>}
      </span>
      <span className="mono tabular-nums shrink-0">{price}</span>
    </li>
  );
}

export function MemberCard({ member }: { member: GroupMember }) {
  const size = sizeLabel(member.netContent, member.unit);
  return (
    <article
      className="border border-border-subtle rounded-md p-4 flex flex-col gap-3"
      data-testid="member-card"
    >
      <div className="flex items-start gap-3">
        <ProductThumb src={member.imageUrl ?? null} alt={member.name ?? member.ean13} />
        <div className="min-w-0">
          <p className="font-medium leading-snug line-clamp-2">{member.name ?? '—'}</p>
          <p className="text-sm text-text-secondary truncate">{member.brand ?? '—'}</p>
          {size && <p className="text-xs text-text-tertiary mt-0.5">{size}</p>}
          <p className="mono text-xs text-text-tertiary mt-1">{member.ean13}</p>
        </div>
      </div>
      {member.offers.length > 0 && (
        <ul className="flex flex-col gap-1.5 border-t border-border-subtle pt-2">
          {member.offers.map((o, i) => (
            <OfferLine key={`${o.retailerId ?? 'r'}-${i}`} offer={o} />
          ))}
        </ul>
      )}
    </article>
  );
}

// Side-by-side grid of member cards — shared by the review detail and the
// flagged queue. Each member shows name/brand/size/image + per-retailer offers
// with live retailer-page deep-links.
export function GroupMembersGrid({ members }: { members: GroupMember[] }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="members-grid">
      {members.map((m) => (
        <MemberCard key={m.ean13} member={m} />
      ))}
    </div>
  );
}
